import { Component, Input, OnInit, SimpleChanges, ViewChild, Output, EventEmitter } from '@angular/core';
import { NoteViewModel, RMDAccountLevelViewModel } from '../../models/rmd.models';
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import { UserService } from '../../../services/user.service'; // Import UserService
import { RmdTrackerApiService } from '../../services/rmd-tracker.service';

@Component({
  selector: 'app-account-notes',
  templateUrl: './account-notes.component.html',
  styleUrls: ['./account-notes.component.css']
})
export class AccountNotesComponent implements OnInit {
  @Input() accounts: RMDAccountLevelViewModel[] = [];
  @Output() noteAdded = new EventEmitter<NoteViewModel>(); // Event emitter to notify parent component when notes are updated
  @Output() noteDeleted = new EventEmitter<[string, number]>(); // Event emitter to notify parent component when notes are deleted
  notes: any[] = [];
  noteColumns: any[] = [];
  currentUser: string | null = null; // Store the current user's ID
  impersonatedId: string | null = null; // Store the current user's ID
  popupVisible: boolean = false; // Control the visibility of the popup

  @ViewChild(DxDataGridComponent, { static: false }) dataGrid!: DxDataGridComponent;
  @ViewChild(DxPopupComponent, { static: false }) popup!: DxPopupComponent;

  constructor(private userService: UserService, private rmdTrackerApi: RmdTrackerApiService) {
    this.userService.getUser().subscribe(user => {
      this.currentUser = user?.id ?? null;
      this.impersonatedId = user?.impersonatedId ?? null;
    });
  }

  ngOnInit() {
    // Extract notes from all accounts
    this.notes = this.accounts.flatMap(account => account.notes || []);

    // Define columns for the DataGrid
    this.noteColumns = [
      { dataField: 'accountName', caption: 'Account' },
      { dataField: 'fullName', caption: 'Created By' },
      { dataField: 'dateCreated', caption: 'Date Created', dataType: 'date', format: 'MM/dd/yyyy' },
      { dataField: 'notes', caption: 'Note'},
      { dataField: 'preserve', caption: 'Preserve', dataType: 'boolean' },
      {
        caption: 'Actions',
        cellTemplate: 'deleteButtonTemplate',
        width: 100
      }
    ];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['accounts']?.currentValue) {
      //console.log('Received accounts:', this.accounts);
      this.notes = this.accounts.flatMap(account =>
        (account.notes || []).map((note: NoteViewModel) => ({
        ...note,
        accountName: account.accountName,
        })
        ));
    }
  }

  // Lifecycle hook to clean up isDeleted notes
  ngOnDestroy() {
    //console.log('AccountNotesComponent is being destroyed. Cleaning up isDeleted notes.');
    this.cleanUpOnHidden();
  }

  cleanUpOnHidden() {
    this.accounts.forEach(account => {
      if (account.notes) {
        account.notes = account.notes.filter(note => !note.isDeleted);
      }
    });
  }

  // Refresh the data when the popup is opened
  refreshData() {
    if (this.dataGrid) {
      //console.log('Refreshing data grid');
      this.dataGrid.instance.refresh();
    }
  }

  // Method to check if the delete button should be enabled
  canDelete(note: NoteViewModel): boolean {
    //console.log("currentUser", this.currentUser);
    //console.log("impersonatedBy", this.impersonatedId);
    const whoAmI = (this.impersonatedId) ? this.impersonatedId : this.currentUser;
    return (note.impersonatorLoginCreated ? note.impersonatorLoginCreated === whoAmI : note.systemLoginCreated === whoAmI);
  }

  trackByNoteId(index: number, note: NoteViewModel): number {
    return note.id; // Use the unique `id` of the note to track changes
  }

  // Method to delete a note
  deleteNote(note: any) {
    const account = this.accounts.find(acc => acc.accountId === note.objectId);
    if (!account) {
      console.error('Account not found for note:', note);
      return;
    }

    note.isDeleted = !note.isDeleted; // Toggle the isDeleted property
    let noteViewModel = account?.notes.find(n => n.id === note.id);
    if (!noteViewModel) {
      console.error('Note not found in account:', note);
      return;
    }

    noteViewModel.isDeleted = !noteViewModel?.isDeleted; // Toggle the isDeleted property

    this.rmdTrackerApi.updateNote(noteViewModel).subscribe(
      response => {
        if (response) {
          //console.log('Note updated successfully:', response);

          // Update the account's latest note and notes count
          const validNotesForAccount = account?.notes.filter(n => !n.isDeleted) || [];
          const latestNoteForAccount = validNotesForAccount.reduce((latest, current) => {
            return new Date(current.dateCreated).getTime() > new Date(latest.dateCreated).getTime() ? current : latest;
          }, validNotesForAccount[0]);

          account.latestNote = latestNoteForAccount?.notes ?? '';

          account.notesCount = validNotesForAccount.length;
          // Find the latest note across all accounts
          const allValidNotes = this.accounts.flatMap(acc => acc.notes || []).filter(n => !n.isDeleted);
          const latestGlobalNote = allValidNotes.reduce((latest, current) => {
            return new Date(current.dateCreated).getTime() > new Date(latest.dateCreated).getTime() ? current : latest;
          }, allValidNotes[0]);

          // Emit the globally latest note as a string
          const incrementValue = response.isDeleted ? -1 : 1;
          this.noteDeleted.emit([latestGlobalNote?.notes || '', incrementValue]);

          this.refreshData();
        }
      },
      error => {
        console.error('Error updating note:', error);
      }
    );
    //console.log(`Deleting note: ${note.notes}`);
  }

  // Method to open the popup
  openPopup() {
    this.popupVisible = true;
  }

  // Method to close the popup
  closePopup() {
    this.popupVisible = false;
  }
  // Method to handle note added event
  onNoteAdded(newNote: NoteViewModel) {
    this.popupVisible = false;
    const account = this.accounts.find(acc => acc.accountId === newNote.objectId);
    if (account) {
      if (!account.notes) {
        account.notes = [];
      }
      account.notes.push(newNote);
      account.latestNote = newNote.notes;
      account.notesCount = account.notes.length;
      this.notes.push({ ...newNote, accountName: account.accountName });
      this.refreshData();
      this.noteAdded.emit(newNote); // Emit the event to notify parent component
    }
  }
}
